import MetaTags from '@components/common/MetaTags';

/* eslint-disable @typescript-eslint/ban-ts-comment */
import '../styles/globals.css';

import dynamic from 'next/dynamic';
import WithAuth from '@components/Auth/WithAuth';
import { TeamsGate } from '@components/TeamsGate';
import { getLayout } from '@components/Layout/LayoutConfiguration';
import Head from 'next/head';
import ErrorBoundaryWrapper from '@components/Errors/ErrorBoundaryWrapper';
import { ThemeProvider } from 'next-themes';
import GoogleTagManager from '@components/common/GoogleTagManager';
import { HeroUIProvider } from '@heroui/react';
import { BaseLoader } from '@components/LoadingContext';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CelloComponent from '@components/celloComponent';
import { GoogleOAuthProvider } from '@react-oauth/google';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { ProduktlyProvider } from '@lib/providers/Produktly';
import EmbedScaler from '@components/Layout/EmbedScaler';
import useIsEmbed from '@embed/useIsEmbed';
import { useOffice } from '@components/OfficeLoader';
import usePowerPointFirestoreResilience from '@lib/hooks/usePowerPointFirestoreResilience';

const HiddenZoomComponent = dynamic(
  () => import('@components/HiddenZoomComponent'),
  { ssr: false }
);

const ZoomEffects = dynamic(
  () => import('@zoom-app/components/ZoomEffects'),
  { ssr: false }
);

const queryClient = new QueryClient();

// Check that PostHog is client-side (used to handle Next.js SSR).
if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY && !window.PUPPETEER) {
  // Session recording spins up a blob-URL web worker (image-bitmap-data-url-worker)
  // that throws source-map access-control errors in mobile Safari/WebKit. Disable
  // recording on mobile browsers where the replay value is low and the noise is high.
  // iPadOS 13+ reports a desktop Safari UA, so also treat touch-capable Macs as mobile.
  const isMobileBrowser =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    person_profiles: 'always',
    disable_session_recording: isMobileBrowser,
    // Enable debug mode in development
    loaded: (posthog) => {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') posthog.debug(false);
    }
  });
}

const OfficeLoader = dynamic(
  () => import('@components/OfficeLoader'),
  { ssr: false }
);

const EmbedScalerGate = () => {
  const isEmbed = useIsEmbed();

  // Only zoom the stage in embed contexts (PowerPoint add-in, Gamma, Canva iframes).
  if (!isEmbed) return null;

  return <EmbedScaler target="ppt-stage" />;
};

// Inner component so the hook only ever runs inside the PowerPoint add-in — cycling
// the Firestore network drops/refetches every listener, so it must not run on the web.
const FirestoreResilienceEffect = () => {
  usePowerPointFirestoreResilience();
  return null;
};

const PowerPointFirestoreResilience = () => {
  const { isPowerpoint } = useOffice();

  if (!isPowerpoint) return null;

  return <FirestoreResilienceEffect />;
};

function MyApp({ Component, pageProps, router }) {
  const layout = getLayout(router.pathname);

  return (
    <div id="ppt-stage" style={{ overflowX: 'hidden' }}>
      <Head>
        <link rel="apple-touch-icon" sizes="180x180" href="/images/icons/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/images/icons/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/images/icons/favicon-16x16.png" />
        <link rel="shortcut icon" href="/images/icons/favicon.ico" />
        <MetaTags pageProps={pageProps} router={router} />
        {/* <script
          src="https://cdn.jsdelivr.net/npm/react-render-tracker"
          data-config="inpage:true"
        ></script> */}
        {!['/login', '/signup', '/sandbox/interactions-generator', '/sandbox/audience-engagement-ai'].includes(router.pathname) && <meta name="viewport" content="width=1440" />}
      </Head>
      <OfficeLoader />
      <PostHogProvider client={posthog}>
        <GoogleTagManager />
        <ThemeProvider attribute="class" enableSystem={true} enableColorScheme={false} defaultTheme="system" forcedTheme={Component.theme || undefined} >
          <HeroUIProvider>
            <QueryClientProvider client={queryClient}>
              <ErrorBoundaryWrapper>
                <TeamsGate>
                  <ZoomEffects />
                  <WithAuth router={router}>
                    <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_AUTH_GOOGLE_ID}>
                      <CelloComponent />
                      <ProduktlyProvider />
                      <EmbedScalerGate />
                      <PowerPointFirestoreResilience />
                      {layout({ children: <Component {...pageProps} /> })}
                      {/* <OnboardingChecklist /> */}
                      <BaseLoader />
                      <ToastContainer position='top-center' theme='colored' />
                    </GoogleOAuthProvider>
                  </WithAuth>
                </TeamsGate>
              </ErrorBoundaryWrapper>
              <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
          </HeroUIProvider>
        </ThemeProvider>
      </PostHogProvider>
      <HiddenZoomComponent />
    </div>
  );
}
export default MyApp;
